<template>
    <div>
        <section
            v-if="shouldShowProductUpdates && currentUpdate"
            class="ProductUpdatesHost"
            role="status"
            aria-live="polite"
        >
            <div class="ProductUpdatesHost__eyebrow">
                <span>Product update</span>
                <div class="ProductUpdatesHost__navigation">
                    <NewButton
                        v-if="unreadUpdates.length > 1"
                        size="icon-xs"
                        variant="flat"
                        color="neutral"
                        class="ProductUpdatesHost__navButton"
                        :disabled="!hasPreviousUpdate"
                        aria-label="Previous product update"
                        @click="previousUpdate"
                    >
                        <FontAwesomeIcon :icon="faAngleLeft" />
                    </NewButton>
                    <span>{{ currentIndex + 1 }} of {{ unreadUpdates.length }}</span>
                    <NewButton
                        v-if="unreadUpdates.length > 1"
                        size="icon-xs"
                        variant="flat"
                        color="neutral"
                        class="ProductUpdatesHost__navButton"
                        :disabled="!hasNextUpdate"
                        aria-label="Next product update"
                        @click="nextUpdate"
                    >
                        <FontAwesomeIcon :icon="faAngleRight" />
                    </NewButton>
                </div>
            </div>
            <h2 class="ProductUpdatesHost__title">
                {{ currentUpdate.title }}
            </h2>
            <p class="ProductUpdatesHost__description">
                {{ currentUpdate.description }}
            </p>
            <p v-if="errorMessage" class="ProductUpdatesHost__error">
                {{ errorMessage }}
            </p>
            <div class="ProductUpdatesHost__actions">
                <NewButton
                    size="xs"
                    variant="flat"
                    color="primary"
                    data-test="learn-more"
                    @click="openKb"
                >
                    Learn more
                </NewButton>
                <NewButton
                    size="xs"
                    variant="flat"
                    color="neutral"
                    :loading="isDismissingAll"
                    @click="dismissAll"
                >
                    Dismiss all
                </NewButton>
                <NewButton
                    size="xs"
                    color="primary"
                    :loading="isDismissing"
                    @click="dismiss"
                >
                    Dismiss
                </NewButton>
            </div>
        </section>
        <ProductUpdatesArchive v-if="shouldShowProductUpdates && archiveOpen" />
    </div>
</template>

<style lang="scss">
.ProductUpdatesHost {
    position: fixed;
    right: 24px;
    bottom: 24px;
    z-index: 99990;
    display: grid;
    gap: 10px;
    width: min(360px, calc(100vw - 32px));
    padding: 18px;
    border: 1px solid #d8e2e7;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 18px 48px rgba(20, 37, 54, 0.18);

    &__eyebrow {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        color: #637386;
        font-size: 12px;
        line-height: 1.25;
        font-weight: 700;
        text-transform: uppercase;
    }

    &__title {
        margin: 0;
        color: #24364a;
        font-size: 18px;
        line-height: 1.25;
        font-weight: 700;
    }

    &__navigation {
        display: inline-flex;
        align-items: center;
        justify-content: flex-end;
        gap: 6px;
        min-height: 32px;
    }

    &__navButton {
        flex: 0 0 auto;
    }

    &__description {
        margin: 0;
        color: #425367;
        font-size: 14px;
        line-height: 1.45;
    }

    &__error {
        margin: 0;
        color: #b23b3b;
        font-size: 13px;
        line-height: 1.35;
    }

    &__actions {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        flex-wrap: wrap;
        gap: 8px;
    }
}

@media (max-width: 640px) {
    .ProductUpdatesHost {
        right: 16px;
        bottom: 16px;
    }
}
</style>

<script lang="ts">
    import { Component, Vue, Watch } from '@evidentid/vue-property-decorator';

    import { faAngleLeft, faAngleRight } from '@fortawesome/pro-light-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import { useUserStore } from '@evidentid/dashboard-commons/modules/auth/store/user';
    import { ProductUpdate } from '@evidentid/tprm-portal-lib/models/product-updates';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import NewButton from '@/components/new-button/NewButton.vue';
    import { useProductUpdatesStore } from '@/modules/product-updates/store';

    import ProductUpdatesArchive from './ProductUpdatesArchive.vue';

    const STANDALONE_ROUTES = [
        'auth.login',
        'auth.check',
        'auth.verify',
        'auth.terms',
        'auth.terms.redirect',
        'auth.register',
        'auth.resetPassword',
        'error',
        'error.rp',
        'error.application',
        'error.generic',
        'examples',
    ];

    @Component({
        components: { FontAwesomeIcon, NewButton, ProductUpdatesArchive },
    })
    export default class ProductUpdatesHost extends Vue {
        private currentIndex = 0;
        private dismissAllErrorForUpdateId: string | null = null;
        private faAngleLeft = faAngleLeft;
        private faAngleRight = faAngleRight;

        private get productUpdatesStore() {
            return useProductUpdatesStore();
        }

        private get productUpdates(): ProductUpdate[] {
            return this.productUpdatesStore.productUpdates;
        }

        private get unreadUpdates(): ProductUpdate[] {
            return this.productUpdates.filter(({ dismissed }) => !dismissed);
        }

        private get currentUpdate(): ProductUpdate | null {
            return this.unreadUpdates[this.currentIndex] ?? this.unreadUpdates[0] ?? null;
        }

        private get hasPreviousUpdate(): boolean {
            return this.currentIndex > 0;
        }

        private get hasNextUpdate(): boolean {
            return this.currentIndex < this.unreadUpdates.length - 1;
        }

        private get userEmail(): string {
            return useUserStore().data?.email || '';
        }

        private get archiveOpen(): boolean {
            return this.productUpdatesStore.archiveOpen;
        }

        private get shouldShowProductUpdates(): boolean {
            return Boolean(this.userEmail) && !this.isStandaloneRoute;
        }

        private get isStandaloneRoute(): boolean {
            const routeName = this.$route?.name;
            const name = routeName ? String(routeName) : '';
            return STANDALONE_ROUTES.some((routeName) => name === routeName || name.startsWith(`${routeName}.`));
        }

        private get isDismissing(): boolean {
            if (!this.currentUpdate) {
                return false;
            }
            return this.productUpdatesStore.dismissStatus[this.currentUpdate.id] === OperationStatus.loading;
        }

        private get isDismissingAll(): boolean {
            return this.productUpdatesStore.dismissAllStatus === OperationStatus.loading;
        }

        private get errorMessage(): string {
            if (this.currentUpdate &&
                this.productUpdatesStore.dismissStatus[this.currentUpdate.id] === OperationStatus.error) {
                return 'We could not dismiss this update. Try again.';
            }
            if (this.productUpdatesStore.dismissAllStatus === OperationStatus.error &&
                this.dismissAllErrorForUpdateId === this.currentUpdate?.id) {
                return 'We could not dismiss all updates. Try again.';
            }
            return '';
        }

        @Watch('userEmail', { immediate: true })
        private loadProductUpdatesForUser(newEmail: string, oldEmail?: string): void {
            if (oldEmail && newEmail !== oldEmail) {
                this.productUpdatesStore.resetProductUpdates();
            }
            this.syncProductUpdates();
        }

        @Watch('$route.name')
        private loadProductUpdatesOnRouteChange(): void {
            this.syncProductUpdates();
        }

        @Watch('unreadUpdates.length')
        private keepCurrentIndexInRange(): void {
            if (this.currentIndex >= this.unreadUpdates.length) {
                this.currentIndex = Math.max(0, this.unreadUpdates.length - 1);
                this.dismissAllErrorForUpdateId = null;
            }
        }

        private syncProductUpdates(): void {
            if (this.shouldShowProductUpdates) {
                this.productUpdatesStore.loadProductUpdates();
            } else if (!this.userEmail) {
                this.productUpdatesStore.resetProductUpdates();
            } else if (this.archiveOpen) {
                this.productUpdatesStore.closeProductUpdatesArchive();
            }
        }

        private openKb(): void {
            if (!this.currentUpdate) {
                return;
            }

            const articleId = /\/articles\/(\d+)/.exec(this.currentUpdate.kbUrl)?.[1];
            if (articleId) {
                // An article opened from here has no fragment; one left by an earlier in-panel link would
                // otherwise follow the reader onto a different article.
                const { kbAnchor, ...query } = this.$route.query;
                this.$router.push({ ...this.$route, query: { ...query, kb: articleId } });
            } else {
                // Not every update points at an article -- some link to a marketing page.
                window.open(this.currentUpdate.kbUrl, '_blank', 'noopener,noreferrer');
            }
        }

        private previousUpdate(): void {
            if (this.hasPreviousUpdate) {
                this.currentIndex -= 1;
                this.dismissAllErrorForUpdateId = null;
            }
        }

        private nextUpdate(): void {
            if (this.hasNextUpdate) {
                this.currentIndex += 1;
                this.dismissAllErrorForUpdateId = null;
            }
        }

        private dismiss(): void {
            if (this.currentUpdate) {
                this.productUpdatesStore.dismissProductUpdate({ id: this.currentUpdate.id });
            }
        }

        private dismissAll(): void {
            this.dismissAllErrorForUpdateId = this.currentUpdate?.id ?? null;
            this.productUpdatesStore.dismissAllProductUpdates();
        }
    }
</script>
